import { useRef, useState } from "react";

import { useEventListener, useRunOnceWhen } from "@nens/react/hooks";

export const useFullscreen = (initialFullscreen?: boolean) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [fullscreen, _setFullscreen] = useState(!!initialFullscreen);

  const setFullscreen = async (fullscreen: boolean) => {
    _setFullscreen(fullscreen);

    if (!fullscreen) {
      if (document.fullscreenElement === mapContainerRef.current) {
        await document.exitFullscreen();
      }
      return;
    }

    // Already the active fullscreen element, nothing to do.
    if (document.fullscreenElement === mapContainerRef.current) return;

    // Another element is fullscreen — exit it first, then claim fullscreen.
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    }
    mapContainerRef.current?.requestFullscreen();
  };

  useRunOnceWhen(!!initialFullscreen && !!mapContainerRef.current, () => {
    setFullscreen(true);
  });

  // Keep fullscreen in sync with the browser's actual fullscreen state.
  // Just updating on toggle isn't enough, because the user can also exit fullscreen
  // by pressing "Escape" or "f11". The event must be attached to `document` because
  // `fullscreenchange` bubbles to document but not to window.
  useEventListener(
    "fullscreenchange",
    () => {
      setFullscreen(document.fullscreenElement === mapContainerRef.current);
    },
    document
  );

  return {
    mapContainerRef,
    fullscreen,
    setFullscreen,
  } as const;
};
